const fieldTypes = {
  text: "text",
  date: "date",
  number: "number",
  address: "address",
  selectlist: "selectlist",
  fileUpload: "fileUpload",
};

const fieldTypeIDToFieldType = {
  1: fieldTypes.text,
  2: fieldTypes.date,
  3: fieldTypes.number,
  4: fieldTypes.address,
  5: fieldTypes.selectlist,
  6: fieldTypes.fileUpload,
  7: fieldTypes.selectlist,
};

const rawToFieldDrag = (data) => {
  if (!data) return [];
  return data.map((obj) => {
    const { networkFieldID: id, title } = obj;
    return {
      id,
      children: title,
      ...obj,
    };
  });
};

const rawToFnrOptionCards = (data) => {
  if (!data) return [];
  return data.map((obj) => {
    const { networkFieldID: id, title } = obj;
    return {
      id,
      label: title,
      ...obj,
    };
  });
};

const payloadToflat = ({
  fieldsInGroup = [],
  fieldsOnItem = [],
  fieldsInParentGroups = [],
  fieldsInEveryoneGroup = [],
  availableFields: {
    defaultFields = [],
    networkFields = [],
    thisSignup = [],
    otherSignups = [],
    profiles = [],
  } = {},
} = {}) => {
  const fullArray = [
    // mapping an "isActive: true" here give us an efficiant way to see what is active in the group.
    ...profiles.map((elem) => ({ ...elem, fromProfile: true })),
    ...fieldsInGroup.map((elem) => ({ ...elem, isActive: true })),
    ...fieldsOnItem.map((elem) => ({ ...elem, isActive: true })),
    ...defaultFields,
    ...networkFields,
    ...thisSignup,
    ...otherSignups.map((elem) => ({ ...elem, copyFromOtherSignup: true })),
    ...fieldsInParentGroups.map((elem) => ({ ...elem, isAnotherGroup: true })),
    ...fieldsInEveryoneGroup.map((elem) => ({
      ...elem,
      groupName: "everyone",
      isAnotherGroup: true,
    })),
  ];

  const entries = fullArray.map((properties) => {
    const { networkFieldID } = properties;
    return [networkFieldID, properties];
  });

  const obj = entries.reduce((acc, cur) => {
    // handle situations where the same field data is in multiple source array
    // Ex. profiles and fieldsOnItem can have duplicates
    if (acc[cur[0]]) {
      acc[cur[0]] = { ...acc[cur[0]], ...cur[1] };
    } else {
      acc[cur[0]] = cur[1];
    }

    return acc;
  }, {});

  return obj;
};

/**
 * Trims the configuration section of the payload down to only what is required for the given field type
 * @param {object} configuration
 * @param {string} fieldType
 */
const trimConfigurationByFieldType = (configuration, fieldType) => {
  const { allowMultiple } = configuration?.behaviorOptions || {};
  const { addr1, city, country, state, zip, year } =
    configuration?.subfields || {};
  const {
    responseOptionList: rawList,
    min,
    max,
  } = configuration?.responseOptions || {};
  const defaultCountry = configuration?.responseOptions?.default?.country;
  switch (fieldType) {
    case fieldTypes.address:
      return {
        subfields: { addr1, city, country, state, zip },
        responseOptions: { default: { country: defaultCountry } },
      };
    case fieldTypes.text:
      if (configuration?.behaviorOptions?.displayAs === "multi")
        return { behaviorOptions: { displayAs: "multi" } };
      return { behaviorOptions: {} };
    case fieldTypes.selectlist: {
      const responseOptionList = rawList.map(
        ({ optionListValueID, optionListValue, displayValue }) => ({
          optionListValueID: optionListValueID || false,
          optionListValue: optionListValue || displayValue,
        })
      );
      return {
        // The backend expects this to be a string...but it's a boolean. This should be fixed on the back end.
        behaviorOptions: { allowMultiple: allowMultiple.toString() },
        responseOptions: {
          optionsType: "responseOptionList",
          responseOptionList,
        },
      };
    }
    case fieldTypes.number:
      return { responseOptions: { min, max } };
    case fieldTypes.date:
      return { subfields: { year } };
    case fieldTypes.fileUpload:
      return undefined;
    default:
      console.error(`fieldType of ${fieldType} is not available`);
      return;
  }
};

/**
 * Converts the original data from the getExtendedFields endpoint to the data structure required for enableExtendedField or extendedfield/create
 * @param {object} param0
 * @param {number} param0.groupId - the groupId this should be available to
 * @param {number} param0.signupItemId - the signupItemId this should be available to
 * @param {object} param0.getPayload - the data structure both used by the app and given by getExtendedFields
 * @param {boolean} param0.isCustom - used to determine which fields should be omitted.
 * @returns {object} - a data structure that is ready to be sent to either enableExtendedField or extendedfield/create
 */
const getPayloadToSubmitPayload = ({
  getPayload,
  groupId,
  signupItemId,
  isCustom,
}) => {
  const submitPayload = {};
  if (groupId) submitPayload.enableForGroupID = groupId;
  if (signupItemId) submitPayload.signupListItemID = signupItemId;

  Object.entries(getPayload).forEach(([key, value]) => {
    switch (key) {
      // only include these for group fields
      case "showOnSignUp":
      case "fieldResponseEditPermissionID":
      case "fieldResponseViewPermissionID":
        if (groupId) submitPayload[key] = Number(value);
        break;
      case "iigOn":
        if (groupId) submitPayload.enableIIG = Number(value);
        break;
      case "periodicIIGOn":
        if (groupId) submitPayload.enablePeriodicIIG = Number(value);
        break;
      case "fieldResponseEntity":
        if (groupId) submitPayload[key] = value;
        break;
      // Only include these for signup fields
      case "copyFromOtherSignup":
      case "required":
        if (signupItemId) submitPayload[key] = value;
        break;
      // Only include these for custom fields
      case "title":
      case "icon":
        // About this replace usage...the endpoint doesn't want the fa-, but then it returns the string with the 'fa-'. Thats's not great and should be fixed on the backend.
        if (isCustom) submitPayload[key] = value?.replace(/^fa-/, "");
        break;
      // Nuance per fieldType
      case "configuration":
        if (isCustom) {
          const fieldType = fieldTypeIDToFieldType[getPayload.fieldTypeID];
          submitPayload[key] = trimConfigurationByFieldType(value, fieldType);
        }
        break;
      // Don't include these at all
      case "blockSharing":
      case "visibility":
      case "createdByID":
      case "orderNumber":
      case "uniqueKey":
      case "groupID":
      case "isCustomField":
      case "notDeletable":
      case "parentShared":
        break;
      default:
        submitPayload[key] = value;
    }
  });
  return submitPayload;
};

const extendedFieldDataTransformers = {
  rawToFieldDrag,
  rawToFnrOptionCards,
  fieldTypeIDToFieldType,
  fieldTypes,
  payloadToflat,
  getPayloadToSubmitPayload,
  trimConfigurationByFieldType,
};

export default extendedFieldDataTransformers;
